import { useEffect, useState } from "react";
import { api, fmtBRL, fmtInt, STATUS_LABELS, STATUS_ORDER } from "@/lib/api";
import { FileXls, FilePdf, DownloadSimple, ChartBar, CheckSquare, Square } from "@phosphor-icons/react";

const todayLocal = () => new Date().toLocaleDateString("en-CA");

function startOfWeek() {
  const d = new Date();
  const day = (d.getDay() + 6) % 7;
  d.setDate(d.getDate() - day);
  return d.toLocaleDateString("en-CA");
}

function startOfMonth() {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), 1).toLocaleDateString("en-CA");
}

const PRESETS = [
  { key: "today", label: "Hoje", range: () => [todayLocal(), todayLocal()] },
  { key: "week", label: "Esta Semana", range: () => [startOfWeek(), todayLocal()] },
  { key: "month", label: "Este Mês", range: () => [startOfMonth(), todayLocal()] },
  { key: "custom", label: "Personalizado", range: null },
];

async function downloadBlob(url, params, filename) {
  const res = await api.get(url, { params, responseType: "blob" });
  const href = URL.createObjectURL(res.data);
  const a = document.createElement("a");
  a.href = href;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(href);
}

function TypeColorBlock({ title, block, accent, testid }) {
  return (
    <div className="border border-black bg-white" data-testid={testid}>
      <div className="border-b border-black p-4 flex items-center justify-between">
        <div className="overline text-zinc-500">{title}</div>
        <div className="font-mono font-bold" style={{ color: accent }}>{fmtInt(block.orders_count)} pedidos</div>
      </div>
      {block.by_type.length === 0 ? (
        <div className="p-8 text-center text-zinc-400 text-sm">Nada no período selecionado.</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-[1px] bg-zinc-200">
          {block.by_type.map((t) => (
            <div key={t.type} className="bg-white p-4">
              <div className="flex items-baseline justify-between mb-2">
                <span className="text-xs px-2 py-0.5 bg-zinc-100 border border-zinc-300 uppercase tracking-widest">{t.type}</span>
                <span className="font-mono text-2xl font-semibold" style={{ color: accent }}>{fmtInt(t.total)}</span>
              </div>
              <div className="space-y-1">
                {t.colors.map((c) => (
                  <div key={c.color} className="flex justify-between text-sm border-b border-zinc-100 pb-1">
                    <span className={c.color === "Sem cor definida" ? "text-zinc-400 italic" : ""}>{c.color}</span>
                    <span className="font-mono font-bold">{fmtInt(c.qty)}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function Reports() {
  const [preset, setPreset] = useState("week");
  const [dateFrom, setDateFrom] = useState(startOfWeek());
  const [dateTo, setDateTo] = useState(todayLocal());
  const [collabs, setCollabs] = useState([]);
  const [selected, setSelected] = useState([]);
  const [fromStatus, setFromStatus] = useState("");
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState("");
  const [error, setError] = useState("");
  const [ordFrom, setOrdFrom] = useState(startOfMonth());
  const [ordTo, setOrdTo] = useState(todayLocal());

  useEffect(() => {
    api.get("/collaborators").then(({ data }) => setCollabs(data)).catch(() => setCollabs([]));
  }, []);

  const applyPreset = (p) => {
    setPreset(p.key);
    if (p.range) {
      const [f, t] = p.range();
      setDateFrom(f);
      setDateTo(t);
    }
  };

  const toggleCollab = (name) => {
    setSelected((prev) => prev.includes(name) ? prev.filter((n) => n !== name) : [...prev, name]);
  };

  const params = () => ({
    date_from: dateFrom,
    date_to: dateTo,
    ...(selected.length > 0 ? { collaborators: selected.join(",") } : {}),
    ...(fromStatus ? { from_status: fromStatus } : {}),
  });

  const generate = async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await api.get("/reports/production", { params: params() });
      setReport(data);
    } catch (e) {
      setError(e?.response?.data?.detail || "Erro ao gerar relatório");
    } finally {
      setLoading(false);
    }
  };

  const exportReport = async (format) => {
    setExporting(format);
    try {
      await downloadBlob("/reports/production/export", { ...params(), format }, `relatorio_producao_${dateFrom}_${dateTo}.${format}`);
    } catch {
      setError("Erro ao exportar");
    } finally {
      setExporting("");
    }
  };

  const exportOrders = async () => {
    setExporting("orders");
    try {
      await downloadBlob("/reports/orders/export", { date_from: ordFrom, date_to: ordTo }, `pedidos_${ordFrom}_${ordTo}.xlsx`);
    } catch {
      setError("Erro ao exportar pedidos");
    } finally {
      setExporting("");
    }
  };

  return (
    <div className="p-6 md:p-10">
      <div className="mb-8">
        <div className="overline text-zinc-500 mb-2">ANÁLISE & EXPORTAÇÃO</div>
        <h1 className="font-head text-2xl sm:text-3xl font-black tracking-tight">Relatórios</h1>
      </div>

      {/* ===== Relatório de Produção ===== */}
      <div className="border border-black bg-white p-6 mb-6" data-testid="production-report-filters">
        <div className="flex items-center gap-2 mb-4">
          <ChartBar size={20} weight="bold" />
          <h2 className="font-head text-xl font-bold">Relatório de Produção</h2>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div>
            <div className="overline text-zinc-500 mb-2">PERÍODO</div>
            <div className="flex flex-wrap gap-2 mb-3">
              {PRESETS.map((p) => (
                <button
                  key={p.key}
                  onClick={() => applyPreset(p)}
                  className={`px-3 py-1.5 border text-xs font-bold uppercase tracking-widest ${preset === p.key ? "bg-black text-white border-black" : "border-zinc-300 hover:border-black"}`}
                  data-testid={`preset-${p.key}`}
                >
                  {p.label}
                </button>
              ))}
            </div>
            <div className="flex items-center gap-3">
              <input type="date" className="input font-mono" value={dateFrom} onChange={(e) => { setDateFrom(e.target.value); setPreset("custom"); }} data-testid="report-date-from" />
              <span className="text-zinc-400">até</span>
              <input type="date" className="input font-mono" value={dateTo} onChange={(e) => { setDateTo(e.target.value); setPreset("custom"); }} data-testid="report-date-to" />
            </div>
            <div className="mt-4">
              <div className="overline text-zinc-500 mb-2">ETAPA DO KANBAN (ORIGEM DA MOVIMENTAÇÃO)</div>
              <select className="input" value={fromStatus} onChange={(e) => setFromStatus(e.target.value)} data-testid="report-from-status">
                <option value="">Todas as etapas</option>
                {STATUS_ORDER.filter((s) => s !== "enviado").map((s) => (
                  <option key={s} value={s}>{`Avançou de: ${STATUS_LABELS[s]}`}</option>
                ))}
              </select>
              <div className="text-xs text-zinc-500 mt-1">Mostra apenas pedidos que saíram da etapa escolhida no período.</div>
            </div>
          </div>

          <div>
            <div className="overline text-zinc-500 mb-2">COLABORADORES {selected.length > 0 ? `· ${selected.length} selecionado${selected.length > 1 ? "s" : ""}` : "· TODOS"}</div>
            {collabs.length === 0 ? (
              <div className="text-sm text-zinc-400">Nenhum colaborador cadastrado.</div>
            ) : (
              <div className="flex flex-wrap gap-2 max-h-32 overflow-y-auto" data-testid="collab-checklist">
                {collabs.map((c) => {
                  const on = selected.includes(c.name);
                  return (
                    <button
                      key={c.id}
                      onClick={() => toggleCollab(c.name)}
                      className={`flex items-center gap-2 px-3 py-2 border text-xs ${on ? "bg-black text-white border-black" : "border-zinc-300 hover:border-black"}`}
                      data-testid={`collab-check-${c.name}`}
                    >
                      {on ? <CheckSquare size={14} weight="fill" /> : <Square size={14} />}
                      {c.name}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {error && <div className="border border-[#E60000] bg-red-50 text-[#E60000] text-sm px-3 py-2 mt-4">{error}</div>}

        <div className="flex flex-wrap gap-3 mt-5">
          <button className="btn" onClick={generate} disabled={loading} data-testid="generate-report-btn">
            <ChartBar size={16} weight="bold" /> {loading ? "Gerando..." : "Gerar Relatório"}
          </button>
          <button className="btn btn-ghost" onClick={() => exportReport("xlsx")} disabled={!!exporting} data-testid="export-xlsx-btn">
            <FileXls size={16} weight="bold" /> {exporting === "xlsx" ? "Exportando..." : "Excel"}
          </button>
          <button className="btn btn-ghost" onClick={() => exportReport("pdf")} disabled={!!exporting} data-testid="export-pdf-btn">
            <FilePdf size={16} weight="bold" /> {exporting === "pdf" ? "Exportando..." : "PDF"}
          </button>
        </div>
      </div>

      {/* ===== Preview ===== */}
      {report && (
        <div className="space-y-6 mb-10" data-testid="report-preview">
          <div className="grid grid-cols-2 md:grid-cols-6 gap-[1px] bg-black border border-black">
            {[
              ["VENDAS · PEDIDOS", fmtInt(report.sales.count), null],
              ["VENDAS · BRUTO", fmtBRL(report.sales.total), null],
              ["VENDAS · LÍQUIDO", fmtBRL(report.sales.net), "#00A300"],
              ["PEDIDOS MOVIMENTADOS", fmtInt(report.moved.orders_count), null],
              ["MOVIMENTAÇÕES", fmtInt(report.moved.movements_count), null],
              ["FINALIZADOS", fmtInt(report.finished.orders_count), "#002FA7"],
            ].map(([label, value, accent]) => (
              <div key={label} className="bg-white p-4">
                <div className="overline text-zinc-500 text-[9px] mb-2">{label}</div>
                <div className="font-mono text-xl font-semibold break-words" style={accent ? { color: accent } : undefined}>{value}</div>
              </div>
            ))}
          </div>

          <TypeColorBlock title="MOVIMENTADO NO PERÍODO · POR TIPO E COR" block={report.moved} accent="#E60000" testid="report-moved-block" />
          <TypeColorBlock title="FINALIZADO (ENVIADO) · POR TIPO E COR" block={report.finished} accent="#002FA7" testid="report-finished-block" />

          {/* Movimentações detalhadas */}
          <div className="border border-black bg-white" data-testid="report-movements">
            <div className="border-b border-black p-4 flex items-center justify-between flex-wrap gap-2">
              <div className="overline text-zinc-500">
                MOVIMENTAÇÕES DETALHADAS
                {report.from_status ? ` · SAÍRAM DE ${STATUS_LABELS[report.from_status]?.toUpperCase()}` : ""}
              </div>
              <span className="font-mono text-sm font-bold">{fmtInt(report.moved.movements_count)} movimentações</span>
            </div>
            {(report.moved.movements || []).length === 0 ? (
              <div className="p-8 text-center text-zinc-400 text-sm">Nenhuma movimentação no período/filtros.</div>
            ) : (
              <div className="max-h-96 overflow-y-auto">
                <table className="table">
                  <thead><tr><th>Data</th><th>Pedido</th><th>Cliente</th><th>Colaborador</th><th>De → Para</th></tr></thead>
                  <tbody>
                    {report.moved.movements.map((m, i) => (
                      <tr key={`${m.at}-${m.bling_number}-${i}`} data-testid={`movement-row-${i}`}>
                        <td className="font-mono text-xs">{new Date(m.at).toLocaleString("pt-BR")}</td>
                        <td className="font-mono font-bold">#{m.bling_number}</td>
                        <td className="max-w-[200px] truncate">{m.customer}</td>
                        <td className="font-semibold">{m.actor}</td>
                        <td className="text-xs">
                          <span className="text-zinc-500">{STATUS_LABELS[m.from] || m.from}</span>
                          <span className="mx-1">→</span>
                          <span className="font-bold">{STATUS_LABELS[m.to] || m.to}</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {report.moved.movements_count > report.moved.movements.length && (
                  <div className="p-3 text-center text-xs text-zinc-500 border-t border-zinc-200">
                    Mostrando as {report.moved.movements.length} mais recentes de {fmtInt(report.moved.movements_count)}. Exporte para ver todas.
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="border border-black bg-white" data-testid="report-by-collab">
              <div className="border-b border-black p-4 overline text-zinc-500">MOVIMENTAÇÕES POR COLABORADOR</div>
              {report.by_collaborator.length === 0 ? (
                <div className="p-8 text-center text-zinc-400 text-sm">Sem movimentações.</div>
              ) : (
                <table className="table">
                  <thead><tr><th>Colaborador</th><th className="text-right">Movimentações</th></tr></thead>
                  <tbody>
                    {report.by_collaborator.map((c) => (
                      <tr key={c.name}><td className="font-semibold">{c.name}</td><td className="text-right font-mono font-bold">{fmtInt(c.movements)}</td></tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            <div className="border border-black bg-white" data-testid="report-sales-by-day">
              <div className="border-b border-black p-4 overline text-zinc-500">VENDAS POR DIA</div>
              {report.sales.by_day.length === 0 ? (
                <div className="p-8 text-center text-zinc-400 text-sm">Sem vendas no período.</div>
              ) : (
                <div className="max-h-72 overflow-y-auto">
                  <table className="table">
                    <thead><tr><th>Dia</th><th className="text-right">Pedidos</th><th className="text-right">Líquido</th></tr></thead>
                    <tbody>
                      {report.sales.by_day.map((d) => (
                        <tr key={d.day}>
                          <td className="font-mono text-xs">{new Date(d.day + "T12:00:00").toLocaleDateString("pt-BR")}</td>
                          <td className="text-right font-mono">{fmtInt(d.count)}</td>
                          <td className="text-right font-mono text-[#00A300]">{fmtBRL(d.net)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ===== Exportação de Pedidos ===== */}
      <div className="border border-black bg-white p-6" data-testid="orders-export-section">
        <div className="flex items-center gap-2 mb-4">
          <DownloadSimple size={20} weight="bold" />
          <h2 className="font-head text-xl font-bold">Exportação de Pedidos (Excel)</h2>
        </div>
        <p className="text-sm text-zinc-500 mb-4">Lista completa de pedidos do período: número, data, cliente, marketplace, status, data limite, copos e valores.</p>
        <div className="flex items-center gap-3 flex-wrap">
          <input type="date" className="input font-mono w-auto" value={ordFrom} onChange={(e) => setOrdFrom(e.target.value)} data-testid="orders-export-from" />
          <span className="text-zinc-400">até</span>
          <input type="date" className="input font-mono w-auto" value={ordTo} onChange={(e) => setOrdTo(e.target.value)} data-testid="orders-export-to" />
          <button className="btn" onClick={exportOrders} disabled={!!exporting} data-testid="orders-export-btn">
            <FileXls size={16} weight="bold" /> {exporting === "orders" ? "Exportando..." : "Baixar Excel"}
          </button>
        </div>
      </div>
    </div>
  );
}
